export const MIN_PATTERN_DOTS = 4;
export const PATTERN_DOT_COUNT = 9;
export const MAX_PATTERN_ATTEMPTS = 5;
export const PATTERN_LOCKOUT_MS = 30_000;
export const DEFAULT_LOCK_TIMEOUT_MS = 60_000;
export const ACTIVITY_WRITE_MS = 5_000;

export const APP_LOCK_TIMEOUTS = [
  { ms: 30_000, label: "۳۰ ثانیه" },
  { ms: 60_000, label: "۱ دقیقه" },
  { ms: 300_000, label: "۵ دقیقه" },
];

const NAME_ID_CLAIM = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";

function decodeJwtPayload(token) {
  try {
    const part = String(token).split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function appLockUserKey(user) {
  if (!user || typeof user !== "object") return null;
  const id = user.tradingPersonId ?? user.id ?? user.userId ?? user.personId ?? user.username ?? user.userName ?? user.phoneNumber;
  if (id != null && id !== "") return String(id);

  const payload = decodeJwtPayload(user.token || user.verificationToken);
  const claim = payload?.nameid ?? payload?.sub ?? payload?.[NAME_ID_CLAIM] ?? payload?.sid;
  if (claim == null || claim === "") return null;
  return String(claim);
}

export function patternKey(indices) {
  return indices.join("-");
}

export function isPatternLongEnough(pattern) {
  if (!pattern) return false;
  return pattern.split("-").filter((part) => part !== "").length >= MIN_PATTERN_DOTS;
}

/** Dot that sits on the straight line between two grid points, if the gap is exactly one cell. */
export function midpoint(from, to) {
  const fromRow = Math.floor(from / 3);
  const fromCol = from % 3;
  const toRow = Math.floor(to / 3);
  const toCol = to % 3;
  const rowDelta = toRow - fromRow;
  const colDelta = toCol - fromCol;
  if (rowDelta % 2 !== 0 || colDelta % 2 !== 0) return null;
  if (rowDelta === 0 && colDelta === 0) return null;
  return (fromRow + rowDelta / 2) * 3 + (fromCol + colDelta / 2);
}

export function withPassedDots(indices, next) {
  if (indices.includes(next)) return indices;
  const from = indices[indices.length - 1];
  if (from == null) return [next];
  const mid = midpoint(from, next);
  if (mid == null || indices.includes(mid)) return [...indices, next];
  return [...indices, mid, next];
}

export function shouldLock({ enabled, lastActivityAt, timeoutMs, now = Date.now() }) {
  if (!enabled) return false;
  if (!lastActivityAt) return true;
  return now - lastActivityAt >= timeoutMs;
}

export function nextFailure(failedAttempts, now = Date.now()) {
  const next = failedAttempts + 1;
  if (next >= MAX_PATTERN_ATTEMPTS) {
    return { failedAttempts: 0, lockoutUntil: now + PATTERN_LOCKOUT_MS, lockedOut: true };
  }
  return { failedAttempts: next, lockoutUntil: 0, lockedOut: false };
}

export function createSalt() {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function hashPattern(pattern, salt) {
  const data = new TextEncoder().encode(`${salt}:${pattern}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
